import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Card, CardContent,
  Grid, Chip, CircularProgress, Alert, InputAdornment,
  Select, MenuItem, FormControl, InputLabel, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Divider, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getClientes, crearCliente, actualizarCliente, archivarCliente,
  borrarCliente, getComerciales, getMisPermisos, exportarClientesExcel
} from '../api';
import FichaCliente from '../components/FichaCliente';
import * as XLSX from 'xlsx';

const estadoColor: any = {
  activo: 'success',
  inactivo: 'default',
  potencial: 'info'
};

const FICHA_VACIA: any = {
  nombre: '', cif: '', tipo: '', sector: '', estado: 'activo',
  direccion: '', ciudad: '', cp: '', telefono: '', email: '', web: '',
  contacto_nombre: '', contacto_cargo: '', contacto_telefono: '', contacto_email: '',
  contacto_facturacion_nombre: '', contacto_facturacion_email: '',
  forma_pago: '', descuento: 0, comercial_id: '', notas: ''
};

export default function Clientes() {
  const [clientes, setClientes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [divisionFiltro, setDivisionFiltro] = useState('');
  const [permisos, setPermisos] = useState<string[]>([]);
  const [dialog, setDialog] = useState<any>(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const esAdmin = user.rol === 'admin';
  const puedeGestionar = esAdmin || permisos.includes('gestionar_clientes');
  const puedeVerCC = esAdmin || permisos.includes('cost_control_clientes');
  const [fichaId, setFichaId] = useState<number | null>(null);

  const cargar = () => {
    setLoading(true);
    getClientes(search, estado)
      .then(setClientes)
      .catch(() => setError('Error cargando clientes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [search, estado]);

  useEffect(() => {
    if (!esAdmin) getMisPermisos().then(setPermisos).catch(() => setPermisos([]));
  }, []);

  const nuevoCliente = () => setDialog({ ...FICHA_VACIA });
  const exportarExcel = async () => {
    const d: any = await exportarClientesExcel();
    const filas = (d.items || []).map((c: any) => {
      const coste = (c.coste_horas||0) + (c.coste_material||0) + (c.coste_gastos||0);
      return {
        'Cliente': c.nombre || '',
        'CIF': c.cif || '',
        'Dirección': c.direccion || '',
        'CP': c.cp || '',
        'Ciudad': c.ciudad || '',
        'Teléfono': c.telefono || '',
        'Email': c.email || '',
        'Tipo': c.tipo || '',
        'Estado': c.estado || '',
        'Nº máquinas': c.n_maquinas || 0,
        'Nº intervenciones': c.n_intervenciones || 0,
        'Última intervención': (c.ultima_intervencion || '').slice(0, 10),
        'Facturado €': c.facturado || 0,
        'Nº facturas': c.n_facturas || 0,
        'Coste horas €': c.coste_horas || 0,
        'Coste material €': c.coste_material || 0,
        'Gastos €': c.coste_gastos || 0,
        'Coste total €': Math.round(coste * 100) / 100,
        'Margen €': Math.round(((c.facturado||0) - coste) * 100) / 100,
      };
    });
    if (!filas.length) { alert('No hay clientes que exportar'); return; }
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [{wch:32},{wch:12},{wch:28},{wch:7},{wch:16},{wch:13},{wch:26},{wch:10},{wch:9},{wch:12},{wch:16},{wch:17},{wch:13},{wch:11},{wch:13},{wch:15},{wch:10},{wch:13},{wch:12}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
    XLSX.writeFile(wb, `Clientes_HEI_${new Date().toISOString().slice(0,10)}.xlsx`);
  };
  const editarCliente = (c: any) => setDialog({ ...FICHA_VACIA, ...c, comercial_id: c.comercial_id || '' });

  const guardar = async () => {
    if (!dialog.nombre) { alert('El nombre es obligatorio'); return; }
    try {
      const datos = { ...dialog, comercial_id: dialog.comercial_id || null };
      if (dialog.id) await actualizarCliente(dialog.id, datos);
      else await crearCliente(datos);
      setDialog(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar el cliente');
    }
  };

  const toggleArchivar = async (c: any) => {
    const nuevoEstado = c.estado === 'inactivo' ? 'activo' : 'inactivo';
    const accion = nuevoEstado === 'inactivo' ? 'archivar' : 'reactivar';
    if (!window.confirm(`¿Seguro que quieres ${accion} a ${c.nombre}?`)) return;
    try { await archivarCliente(c.id, nuevoEstado); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (c: any) => {
    if (!window.confirm(`¿BORRAR DEFINITIVAMENTE a ${c.nombre}? Esta acción no se puede deshacer.`)) return;
    try { await borrarCliente(c.id); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error al borrar'); }
  };

  // Sectores disponibles, sacados de los propios clientes
  const sectores = Array.from(new Set(
    clientes.map((c: any) => c.sector).filter(Boolean)
  )).sort();

  const clientesFiltrados = clientes.filter((c: any) => {
    if (!divisionFiltro) return true;
    const divs = Array.isArray(c.divisiones) ? c.divisiones : [];
    if (divisionFiltro === 'sin') return divs.length === 0;
    return divs.includes(divisionFiltro);
  });
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600}>👤 Clientes</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" startIcon={<span>📊</span>} onClick={exportarExcel}>
            Exportar a Excel
          </Button>
          {puedeGestionar && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={nuevoCliente}
              sx={{ bgcolor: '#4f46e5' }}>
              Nuevo cliente
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Buscar por nombre, CIF, ciudad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={estado} label="Estado" onChange={(e) => setEstado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="activo">Activo</MenuItem>
            <MenuItem value="potencial">Potencial</MenuItem>
            <MenuItem value="inactivo">Archivados</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>División</InputLabel>
          <Select value={divisionFiltro} label="División"
            onChange={(e) => setDivisionFiltro(e.target.value)}>
            <MenuItem value="">Todas las divisiones</MenuItem>
            <MenuItem value="SANY Cranes">SANY Cranes</MenuItem>
            <MenuItem value="SANY Robotics">SANY Robotics</MenuItem>
            <MenuItem value="SANY Marine">SANY Marine</MenuItem>
            <MenuItem value="TII SCHEUERLE">TII SCHEUERLE</MenuItem>
            <MenuItem value="sin">— Sin máquinas —</MenuItem>
          </Select>
        </FormControl>        
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {clientes.length} cliente(s)
      </Typography>

      <Grid container spacing={2}>
        {clientes.map((c) => (
          <Grid item xs={12} sm={6} lg={4} key={c.id}>
            <Card onClick={() => setFichaId(c.id)}
              sx={{ borderRadius: 3, height: 250, cursor: 'pointer',
              display: 'flex', flexDirection: 'column',
              opacity: c.estado === 'inactivo' ? 0.6 : 1,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
            }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: '#4f46e5', width: 40, height: 40 }}>
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                        {c.nombre}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.cif || '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label={c.estado} color={estadoColor[c.estado] || 'default'} size="small" />
                </Box>

                {c.sector && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {c.sector}
                  </Typography>
                )}
                {Array.isArray(c.divisiones) && c.divisiones.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {c.divisiones.map((d: string) => (
                      <Chip key={d} label={d} size="small" variant="outlined"
                        sx={{ fontSize: 10, height: 20 }} />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {c.ciudad && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">{c.ciudad}</Typography>
                    </Box>
                  )}
                  {c.telefono && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">{c.telefono}</Typography>
                    </Box>
                  )}
                  {c.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{c.email}</Typography>
                    </Box>
                  )}
                </Box>

                {puedeGestionar && (
                  <Box onClick={(e) => e.stopPropagation()}
                    sx={{ display: 'flex', gap: 0.5, mt: 1.5, justifyContent: 'flex-end' }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => editarCliente(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={c.estado === 'inactivo' ? 'Reactivar' : 'Archivar'}>
                      <IconButton size="small" onClick={() => toggleArchivar(c)}>
                        {c.estado === 'inactivo' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {esAdmin && (
                      <Tooltip title="Borrar definitivamente">
                        <IconButton size="small" color="error" onClick={() => eliminar(c)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {clientes.length === 0 && !loading && (
        <Alert severity="info">No se encontraron clientes con esos criterios.</Alert>
      )}

      {/* DIALOG FICHA CLIENTE */}
      <Dialog open={!!dialog} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDialog(null); } }} maxWidth="md" fullWidth>
        <DialogTitle>{dialog?.id ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle>
        <DialogContent>
          {dialog && (
            <Box sx={{ mt: 1 }}>
              {/* Datos generales */}
              <Typography variant="subtitle2" sx={{ color: '#4f46e5', mb: 1.5 }}>Datos generales</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nombre / Razón social *" value={dialog.nombre}
                    onChange={(e) => setDialog({ ...dialog, nombre: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="CIF / NIF" value={dialog.cif || ''}
                    onChange={(e) => setDialog({ ...dialog, cif: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Tipo" value={dialog.tipo || ''}
                    onChange={(e) => setDialog({ ...dialog, tipo: e.target.value })}
                    placeholder="Empresa, autónomo..." />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Sector" value={dialog.sector || ''}
                    onChange={(e) => setDialog({ ...dialog, sector: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select value={dialog.estado} label="Estado"
                      onChange={(e) => setDialog({ ...dialog, estado: e.target.value })}>
                      <MenuItem value="activo">Activo</MenuItem>
                      <MenuItem value="potencial">Potencial</MenuItem>
                      <MenuItem value="inactivo">Inactivo / Archivado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Dirección */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ color: '#4f46e5', mb: 1.5 }}>Dirección</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Dirección" value={dialog.direccion || ''}
                    onChange={(e) => setDialog({ ...dialog, direccion: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Ciudad" value={dialog.ciudad || ''}
                    onChange={(e) => setDialog({ ...dialog, ciudad: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Código postal" value={dialog.cp || ''}
                    onChange={(e) => setDialog({ ...dialog, cp: e.target.value })} />
                </Grid>
              </Grid>

              {/* Contacto */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ color: '#4f46e5', mb: 1.5 }}>Contacto</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Teléfono" value={dialog.telefono || ''}
                    onChange={(e) => setDialog({ ...dialog, telefono: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Email" value={dialog.email || ''}
                    onChange={(e) => setDialog({ ...dialog, email: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Web" value={dialog.web || ''}
                    onChange={(e) => setDialog({ ...dialog, web: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Persona de contacto" value={dialog.contacto_nombre || ''}
                    onChange={(e) => setDialog({ ...dialog, contacto_nombre: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Cargo del contacto" value={dialog.contacto_cargo || ''}
                    onChange={(e) => setDialog({ ...dialog, contacto_cargo: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Teléfono del contacto" value={dialog.contacto_telefono || ''}
                    onChange={(e) => setDialog({ ...dialog, contacto_telefono: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email del contacto" value={dialog.contacto_email || ''}
                    onChange={(e) => setDialog({ ...dialog, contacto_email: e.target.value })} />
                </Grid>
              </Grid>

              {/* Facturación y comercial */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ color: '#4f46e5', mb: 1.5 }}>Facturación y comercial</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Contacto de facturación" value={dialog.contacto_facturacion_nombre || ''}
                    onChange={(e) => setDialog({ ...dialog, contacto_facturacion_nombre: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Email de facturación" value={dialog.contacto_facturacion_email || ''}
                    onChange={(e) => setDialog({ ...dialog, contacto_facturacion_email: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Forma de pago" value={dialog.forma_pago || ''}
                    onChange={(e) => setDialog({ ...dialog, forma_pago: e.target.value })}
                    placeholder="30 días, contado..." />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Descuento (%)" type="number" value={dialog.descuento || 0}
                    onChange={(e) => setDialog({ ...dialog, descuento: parseFloat(e.target.value) || 0 })} />
                </Grid>
              </Grid>

              {/* Notas */}
              <Divider sx={{ my: 2 }} />
              <TextField fullWidth label="Notas" multiline rows={3} value={dialog.notas || ''}
                onChange={(e) => setDialog({ ...dialog, notas: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" sx={{ bgcolor: '#4f46e5' }}
            disabled={!dialog?.nombre}>
            {dialog?.id ? 'Guardar cambios' : 'Crear cliente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FICHA 360º DEL CLIENTE */}
      {fichaId && (
        <FichaCliente clienteId={fichaId} puedeVerCC={puedeVerCC}
          onClose={() => setFichaId(null)} />
      )}
    </Box>
  );
}